/**
 * GlobalErrorFallback
 *
 * Full-screen fallback shown by GlobalErrorBoundary when a catastrophic
 * error has occurred at the app level (Redux/Provider/runtime crash).
 *
 * Props:
 *   - error   : The caught Error object (optional)
 */
const GlobalErrorFallback = ({ error }) => {
  const handleReload = () => {
    window.location.reload();
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.card}>
        {/* Icon */}
        <div style={styles.iconWrapper}>
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z"
              fill="#E53E3E"
            />
          </svg>
        </div>

        {/* Title */}
        <h1 style={styles.title}>Application Error</h1>

        {/* Subtitle */}
        <p style={styles.subtitle}>
          Something went wrong and the application couldn't recover automatically.
          <br />
          Try the options below to get back on track.
        </p>

        {/* Error details (collapsed, dev-friendly) */}
        {error && (
          <details style={styles.details}>
            <summary style={styles.detailsSummary}>Error details</summary>
            <pre style={styles.errorText}>{error?.stack ?? error.message}</pre>
          </details>
        )}

        {/* Actions */}
        <div style={styles.actions}>
          <button
            id="global-error-reload-btn"
            style={{ ...styles.btn, ...styles.btnSecondary }}
            onClick={handleReload}
            type="button"
          >
            Reload Page
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #f5f7fa 0%, #e8ecf0 100%)",
    padding: "20px",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  card: {
    background: "#ffffff",
    borderRadius: "16px",
    padding: "48px 40px",
    maxWidth: "480px",
    width: "100%",
    textAlign: "center",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.06)",
    border: "1px solid rgba(0, 0, 0, 0.06)",
  },
  iconWrapper: {
    width: "80px",
    height: "80px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #fff5f5 0%, #ffe8e8 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 24px",
    border: "1px solid #fecaca",
  },
  title: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#1a202c",
    margin: "0 0 12px",
    letterSpacing: "-0.3px",
  },
  subtitle: {
    fontSize: "14px",
    color: "#718096",
    lineHeight: "1.7",
    margin: "0 0 24px",
  },
  details: {
    textAlign: "left",
    background: "#f7fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    padding: "12px 16px",
    marginBottom: "24px",
    cursor: "pointer",
  },
  detailsSummary: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#4a5568",
    userSelect: "none",
  },
  errorText: {
    margin: "8px 0 0",
    fontSize: "12px",
    color: "#e53e3e",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontFamily: "'Fira Code', 'Cascadia Code', 'Consolas', monospace",
  },
  actions: {
    display: "flex",
    gap: "12px",
    justifyContent: "center",
    flexWrap: "wrap",
  },
  btn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px 24px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    border: "none",
    transition: "all 0.15s ease",
    letterSpacing: "0.1px",
  },
  btnSecondary: {
    background: "#f7fafc",
    color: "#4a5568",
    border: "1px solid #e2e8f0",
  },
};

export { GlobalErrorFallback };
